import React, { useState, useEffect, useCallback } from "react";
import "./App.css";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import Header from "./components/Header";
import AboutSection from "./components/AboutSection";
import SkillsSection from "./components/SkillsSection";
import ExperienceSection from "./components/ExperienceSection";
import EducationSection from "./components/EducationSection";
import ResultsSection from "./components/ResultsSection";
import ProjectsSection from "./components/ProjectsSection";
import ContactSection from "./components/ContactSection";
import AnimatedAvatar from "./components/AnimatedAvatar";
import ChatBot from "./components/ChatBot";
import { navItems } from "./data/mock";

function AppContent() {
  const { t } = useLanguage();
  const [activeSection, setActiveSection] = useState('apropos');
  const [doorOpenSection, setDoorOpenSection] = useState(null);
  const [avatarState, setAvatarState] = useState('idle');
  const [bubbleText, setBubbleText] = useState('');
  const [showBubble, setShowBubble] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Remove Emergent badge on mount
  useEffect(() => {
    const removeBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) badge.remove();
      document.querySelectorAll('a[href*="emergent"]').forEach(el => el.remove());
    };
    removeBadge();
    const timers = [100, 500, 1000, 2000].map(t => setTimeout(removeBadge, t));
    return () => timers.forEach(clearTimeout);
  }, []);

  // Navigate to section with avatar animation
  const handleNavigate = useCallback((sectionId) => {
    if (avatarState !== 'idle') return;

    const doorInfo = t.door[sectionId];
    if (!doorInfo) return;

    setActiveSection(sectionId);

    // Step 1: Avatar starts walking
    setAvatarState('walking');
    setBubbleText(`${t.avatar.goingToOpen}: ${doorInfo.title}`);
    setShowBubble(true);

    // Step 2: Scroll to section while avatar walks
    const section = document.getElementById(sectionId);
    if (section) {
      window.scrollTo({
        top: section.offsetTop - 80,
        behavior: 'smooth'
      });
    }

    // Step 3: Avatar opens door
    setTimeout(() => {
      setAvatarState('opening');
      setBubbleText(t.avatar.openingDoor);
      setDoorOpenSection(sectionId);
    }, 800);

    // Step 4: Welcome message
    setTimeout(() => {
      setBubbleText(t.avatar.welcome);
    }, 1400);

    // Step 5: Reset
    setTimeout(() => {
      setShowBubble(false);
      setDoorOpenSection(null);
      setAvatarState('idle');
    }, 2200);

  }, [avatarState, t]);

  // Handle scroll to update active section
  useEffect(() => {
    const handleScroll = () => {
      if (avatarState !== 'idle') return;
      
      const sections = navItems.map(item => item.id);
      const scrollPosition = window.scrollY + 200;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            if (activeSection !== sectionId) {
              setActiveSection(sectionId);
            }
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeSection, avatarState]);

  // Initial animation on load
  useEffect(() => {
    const timer = setTimeout(() => {
      handleNavigate('apropos');
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toggle chat when clicking on avatar
  const handleAvatarClick = () => {
    if (avatarState === 'idle') {
      setIsChatOpen(!isChatOpen);
    }
  };

  return (
    <div className="App">
      <Header activeSection={activeSection} onNavigate={handleNavigate} />
      
      <main>
        <AboutSection isDoorOpen={doorOpenSection === 'apropos'} />
        <SkillsSection isDoorOpen={doorOpenSection === 'competences'} />
        <ExperienceSection isDoorOpen={doorOpenSection === 'experiences'} />
        <EducationSection isDoorOpen={doorOpenSection === 'formations'} />
        <ResultsSection isDoorOpen={doorOpenSection === 'resultats'} />
        <ProjectsSection isDoorOpen={doorOpenSection === 'projets'} />
        <ContactSection isDoorOpen={doorOpenSection === 'contact'} />
      </main>

      {/* Avatar fixed at bottom right, always animated */}
      <div 
        className={`avatar-container ${avatarState} ${isChatOpen ? 'chat-open' : ''}`}
        onClick={handleAvatarClick}
        title={isChatOpen ? t.chat.close : t.chat.title}
      >
        <AnimatedAvatar 
          isMoving={avatarState === 'walking'} 
          isOpeningDoor={avatarState === 'opening'}
        />
        
        {/* Speech Bubble */}
        <div className={`avatar-bubble ${showBubble && !isChatOpen ? 'visible' : ''}`}>
          {bubbleText}
        </div>
        
        {/* Chat indicator */}
        {!isChatOpen && avatarState === 'idle' && (
          <div className="chat-indicator">
            💬
          </div>
        )}
      </div>

      {/* Chatbot */}
      <ChatBot isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

export default App;
